import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createBlock as _createBlock, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = {
  key: 1,
  class: "item-row-heading"
}
const _hoisted_3 = { class: "wc-product-row-modern__title" }
const _hoisted_4 = { class: "wc-product-row-modern__title-text" }
const _hoisted_5 = {
  key: 0,
  class: "wc-product-row-modern__badge"
}
const _hoisted_6 = {
  key: 0,
  class: "wc-item-row__spinner-wrapper d-flex"
}

import axios from 'axios';
import { computed, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import WCProductCardModern from '@/components/WCProductCardModern/WCProductCardModern.vue';
import WCCarouselModern from '@/components/WCCarouselModern/WCCarouselModern.vue';
import WCSpinner from '@/components/WCSpinner/WCSpinner.vue';
import AnalyticsService from '@/services/analytics.service';
import TrackableEventConstants from '@/constants/TrackableEventConstants';
import { ENTRY_METHOD, type EntryMethod } from '@/models/entities/cart/entry-method';
import usePlatform from '@/modules/platform/composables/usePlatform';
import useCardLimitInRow, {
  DEFAULT_CARD_WIDTH_WITH_MARGIN,
  DEFAULT_BUFFER_CARDS,
} from '@/composables/useCardLimitInRow';
import { useI18n } from 'vue-i18n';
import type ItemDetail from '@/models/entities/items/item-detail';

interface Props {
  rowId: string;
  rowName: string;
  rowType?: string;
  rowSizeLimit?: number;
  hideIfEmpty?: boolean;
  entryMethod?: EntryMethod;
  cardWidthWithMargin?: number;
  bufferCards?: number;
  items?: ItemDetail[];
  totalCount?: number;
  seeAllRoute?: string;
  isPersonalized?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'WCProductRowModern',
  props: {
    rowId: {},
    rowName: {},
    rowType: { default: undefined },
    rowSizeLimit: { default: 7 },
    hideIfEmpty: { type: Boolean, default: true },
    entryMethod: { default: ENTRY_METHOD.DEFAULT },
    cardWidthWithMargin: {},
    bufferCards: { default: DEFAULT_BUFFER_CARDS },
    items: { default: undefined },
    totalCount: { default: undefined },
    seeAllRoute: { default: undefined },
    isPersonalized: { type: Boolean, default: false }
  },
  setup(__props: any) {

const props = __props;

const store = useStore();
const loading = ref(false);
const loadFailedError = ref<string | null>(null);
const totalCount = ref(-1);
const products = ref<ItemDetail[]>([]);
const rowRef = ref<HTMLElement | null>(null);
const { platform } = usePlatform();
const productRowClass =  platform.value.isMobile ? 'product-row-class-mobile' : 'product-row-class-desktop' ;
const productRowCarouselClass = platform.value.isMobile ? 'wc-product-row-modern__carousel-mobile' : 'wc-product-row-modern__carousel-desktop' ;
const { t } = useI18n();
const getUser = computed(() => store.getters['user/getUser']);
const isControlled = computed(() => props.items !== undefined);

const viewMoreLink = computed(() => {
  return props.seeAllRoute || `/b?g=${props.rowId}`;
});

const getCardWidthWithMargin = (): number => {
  if (!rowRef.value) {
    return DEFAULT_CARD_WIDTH_WITH_MARGIN;
  }
  const style = getComputedStyle(rowRef.value);
  const cardWidth = parseFloat(style.getPropertyValue('--wc-row-card-width'));
  const gap = parseFloat(style.getPropertyValue('--wc-row-card-gap'));
  return cardWidth && gap ? cardWidth + gap : DEFAULT_CARD_WIDTH_WITH_MARGIN;
};

const getCardLimitInRow = useCardLimitInRow(
  rowRef,
  () => props.rowSizeLimit,
  () => props.cardWidthWithMargin ?? getCardWidthWithMargin(),
  () => props.bufferCards,
);

const fetchData = async (): Promise<void> => {
  const ps = platform.value.isMobile ? props.rowSizeLimit : getCardLimitInRow();
  const req: Record<string, unknown> = {
    ps,
    pn: 1,
  };

  if (props.rowType === 'coupon') {
    req.facets = { cp: [props.rowId] };
  } else {
    req.g = [props.rowId];
  }

  try {
    const response = await axios.post('api/b/', req);
    products.value = response.data.items;
    totalCount.value = response.data.totalCount
      ? response.data.totalCount
      : response.data.items.length;

    AnalyticsService.track(TrackableEventConstants.ItemImpression, {
      items: products.value,
      customerId: getUser.value?.sub,
      listId: props.rowId,
      listName: props.rowName,
    });
  } catch (error) {
    loading.value = false;
    loadFailedError.value = error instanceof Error ? error.message : String(error);
    console.error(error);
  }
};

const updateData = async () => {
  totalCount.value = 0;
  products.value = [];
  loading.value = true;
  loadFailedError.value = null;
  await fetchData();
  loading.value = false;
};

watch(() => props.rowId, () => {
  if (!isControlled.value) {
    updateData();
  }
});

watch(
  () => props.items,
  (newItems) => {
    if (newItems !== undefined) {
      products.value = newItems;
      totalCount.value = props.totalCount ?? newItems.length;
      loading.value = false;
      loadFailedError.value = null;
    }
  },
  { immediate: true },
);

onMounted(() => {
  if (!isControlled.value) {
    updateData();
  }
});

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["item-row", _unref(productRowClass)]),
    ref_key: "rowRef",
    ref: rowRef
  }, [
    (!_ctx.hideIfEmpty || products.value.length > 0)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: _normalizeClass(_unref(productRowClass))
        }, [
          (loadFailedError.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_1, _toDisplayString(loadFailedError.value), 1))
            : _createCommentVNode("", true),
          _cache[3] || (_cache[3] = _createTextVNode()),
          (!loadFailedError.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                _createElementVNode("h2", _hoisted_3, [
                  _createElementVNode("span", _hoisted_4, _toDisplayString(_ctx.rowName), 1)
                ]),
                _cache[0] || (_cache[0] = _createTextVNode()),
                (_ctx.isPersonalized)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_5, _toDisplayString(_unref(t)('personalized')), 1))
                  : _createCommentVNode("", true),
                _cache[1] || (_cache[1] = _createTextVNode()),
                (totalCount.value > products.value.length)
                  ? (_openBlock(), _createBlock(_component_router_link, {
                      key: 1,
                      to: viewMoreLink.value,
                      class: "wc-product-row-modern__view-more-link"
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_unref(t)('seeAll')), 1)
                      ]),
                      _: 1
                    }, 8, ["to"]))
                  : _createCommentVNode("", true)
              ]))
            : _createCommentVNode("", true),
          _cache[4] || (_cache[4] = _createTextVNode()),
          (!loadFailedError.value)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                (loading.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                      _createVNode(WCSpinner, {
                        class: "mx-auto align-self-center",
                        size: "xl"
                      })
                    ]))
                  : _createCommentVNode("", true),
                _cache[2] || (_cache[2] = _createTextVNode()),
                (!loading.value)
                  ? (_openBlock(), _createBlock(WCCarouselModern, {
                      key: 1,
                      class: _normalizeClass(_unref(productRowCarouselClass)),
                      title: _ctx.rowName,
                      showArrows: true
                    }, {
                      default: _withCtx(() => [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(products.value, (product) => {
                          return (_openBlock(), _createBlock(WCProductCardModern, {
                            key: product.id,
                            item: product,
                            class: "wc-product-row-modern__card",
                            role: "listitem"
                          }, null, 8, ["item"]))
                        }), 128))
                      ]),
                      _: 1
                    }, 8, ["class", "title"]))
                  : _createCommentVNode("", true)
              ], 64))
            : _createCommentVNode("", true)
        ], 2))
      : _createCommentVNode("", true)
  ], 2))
}
}

})